import { BudgetItemType } from "../types/budget/budget";

/**
 * Ordem padrão das categorias de orçamento.
 * Categorias não listadas aqui serão ordenadas alfabeticamente após as prioritárias.
 */
export const CATEGORY_PRIORITY_ORDER: BudgetItemType[] = [
  "ACCOMMODATIONS", // Hospedagem
  "AEB", // Alimentos e Bebidas
  "ROOMS", // Salas e Espaços
  "EQUIPMENTS", // Equipamentos
  "AIR", // Aéreo
  "TRANSFER", // Transfer
];

/**
 * Mapa de prioridades para busca O(1)
 */
const PRIORITY_MAP = new Map<string, number>(
  CATEGORY_PRIORITY_ORDER.map((cat, index) => [cat, index])
);

/**
 * Ordena categorias seguindo a ordem padrão.
 * Categorias prioritárias vêm primeiro, demais em ordem alfabética.
 *
 * @param categories - Array de categorias para ordenar
 * @returns Array ordenado de categorias
 */
export function sortCategoriesByPriority(categories: string[]): string[] {
  return [...categories].sort((a, b) => {
    const priorityA = PRIORITY_MAP.get(a);
    const priorityB = PRIORITY_MAP.get(b);

    if (priorityA !== undefined && priorityB !== undefined) {
      return priorityA - priorityB;
    }
    if (priorityA !== undefined) return -1;
    if (priorityB !== undefined) return 1;

    return a.localeCompare(b, "pt-BR");
  });
}

/**
 * Ordena array de objetos pela propriedade de categoria.
 *
 * @param items - Array de objetos com propriedade de categoria
 * @param categoryKey - Nome da propriedade que contém a categoria (padrão: "type")
 * @returns Array ordenado de objetos
 */
export function sortItemsByCategory<T extends Record<string, unknown>>(
  items: T[],
  categoryKey = "type"
): T[] {
  return [...items].sort((a, b) => {
    const catA = a[categoryKey] as string;
    const catB = b[categoryKey] as string;

    const priorityA = PRIORITY_MAP.get(catA);
    const priorityB = PRIORITY_MAP.get(catB);

    if (priorityA !== undefined && priorityB !== undefined) {
      return priorityA - priorityB;
    }
    if (priorityA !== undefined) return -1;
    if (priorityB !== undefined) return 1;

    return catA.localeCompare(catB, "pt-BR");
  });
}

/**
 * Ordenação híbrida usada na EXIBIÇÃO das categorias de um fornecedor.
 *
 * O usuário pode reordenar categorias por drag and drop, e essa escolha é
 * gravada em `order` (ver `REORDER_CATEGORIES`). Enquanto nenhum item do
 * conjunto tiver `order`, a função se comporta exatamente como
 * `sortItemsByCategory` — é o que preserva a ordem histórica de todo orçamento
 * que nunca foi arrastado.
 *
 * Itens sem `order` num conjunto que já foi reordenado (ex.: categoria criada
 * depois do último drag) vão para o fim, entre si na ordem de prioridade.
 *
 * Atenção: isto vale só para a renderização. As listas de OPÇÕES do dropdown
 * "+ Categoria" continuam usando `sortItemsByCategory`, onde a ordem fixa é a
 * correta.
 */
export function sortItemsByCategoryRespectingOrder<
  T extends Record<string, unknown>
>(items: T[], categoryKey = "type", orderKey = "order"): T[] {
  const priorityOrdered = sortItemsByCategory(items, categoryKey);

  const hasExplicitOrder = items.some(
    (item) => typeof item[orderKey] === "number"
  );
  if (!hasExplicitOrder) return priorityOrdered;

  // Desempate estável para itens sem `order`: mantém entre eles a ordem fixa.
  const priorityRank = new Map<T, number>(
    priorityOrdered.map((item, index) => [item, index])
  );

  return [...items].sort((a, b) => {
    const orderA = a[orderKey];
    const orderB = b[orderKey];
    const hasA = typeof orderA === "number";
    const hasB = typeof orderB === "number";

    if (hasA && hasB) return (orderA as number) - (orderB as number);
    if (hasA) return -1;
    if (hasB) return 1;

    return (priorityRank.get(a) ?? 0) - (priorityRank.get(b) ?? 0);
  });
}
